/* Clovia product page: variant picker, colour-driven gallery, sticky add-to-bag.
   Variant data is server-rendered JSON; no extra requests on option change. */

const toText = (html) => new DOMParser().parseFromString(html || '', 'text/html').body.textContent;

class ProductPage extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-variants]');
    this.form = this.querySelector('form[data-product-form]');
    if (!data || !this.form) return;
    this.variants = JSON.parse(data.textContent);
    this.fieldsets = [...this.querySelectorAll('fieldset[data-option]')];
    this.colorIndex = Number(this.dataset.colorPosition || 0) - 1;
    this.groupMedia = this.dataset.groupMedia === 'true';
    this.track = this.querySelector('[data-gallery-track]');
    this.items = [...(this.track?.children || [])];

    this.colorNames = new Set();
    if (this.colorIndex >= 0) this.variants.forEach((v) => this.colorNames.add(v.options[this.colorIndex].toLowerCase().trim()));

    this.form.addEventListener('change', (e) => {
      if (e.target.name && e.target.name.startsWith('option')) this.onOptionChange(e.target);
    });

    this.updateAvailability(this.selected());
    if (this.groupMedia && this.colorIndex >= 0) this.filterMedia(this.selected()[this.colorIndex]);
    this.initCounter();
    this.initSticky();
    this.initReels();
  }

  selected() {
    return this.fieldsets.map((fs) => fs.querySelector('input:checked')?.value);
  }

  findVariant(sel) {
    return this.variants.find((v) => v.options.every((o, i) => o === sel[i]));
  }

  onOptionChange(input) {
    const sel = this.selected();
    this.fieldsets.forEach((fs, i) => {
      const out = fs.querySelector('[data-option-value]');
      if (out) out.textContent = sel[i] || '';
    });
    this.updateAvailability(sel);

    const colorChanged = this.colorIndex >= 0 && input.name === `option${this.colorIndex + 1}`;
    let variant = this.findVariant(sel);
    // Colour clicked but that exact combination doesn't exist: keep the colour, pick its first available size.
    if (!variant && colorChanged) {
      variant = this.variants.find((v) => v.options[this.colorIndex] === sel[this.colorIndex] && v.available)
        || this.variants.find((v) => v.options[this.colorIndex] === sel[this.colorIndex]);
      if (variant) this.check(variant.options);
    }

    if (colorChanged && this.groupMedia) this.filterMedia(sel[this.colorIndex]);
    this.render(variant);
    if (variant && variant.media) this.showMedia(variant.media);
  }

  check(options) {
    this.fieldsets.forEach((fs, i) => {
      const input = [...fs.querySelectorAll('input')].find((el) => el.value === options[i]);
      if (input) input.checked = true;
      const out = fs.querySelector('[data-option-value]');
      if (out) out.textContent = options[i];
    });
    this.updateAvailability(options);
  }

  updateAvailability(sel) {
    this.fieldsets.forEach((fs, i) => {
      fs.querySelectorAll('input').forEach((input) => {
        // A colour is unavailable only when no size of it is in stock; other options
        // are checked against the rest of the current selection.
        const ok = this.variants.some((v) => v.available && v.options[i] === input.value
          && (i === this.colorIndex || v.options.every((o, j) => j === i || o === sel[j])));
        input.nextElementSibling?.classList.toggle('is-unavailable', !ok);
      });
    });
  }

  render(variant) {
    const buttons = this.querySelectorAll('[data-add-button]');
    const idInput = this.form.querySelector('[data-variant-id]');
    const strings = window.Clovia?.strings || {};

    const buyNow = this.querySelector('[data-buy-now]');

    if (!variant) {
      buttons.forEach((b) => { b.disabled = true; b.textContent = strings.unavailable; });
      if (buyNow) buyNow.hidden = true;
      return;
    }

    idInput.value = variant.id;
    buttons.forEach((b) => {
      b.disabled = !variant.available;
      b.textContent = variant.available ? strings.addToBag : strings.soldOut;
    });
    if (buyNow) buyNow.hidden = !variant.available;

    const price = this.querySelector('[data-product-price] [data-price]');
    if (price) {
      const current = price.querySelector('.price__current');
      current.textContent = toText(variant.priceHtml);
      price.classList.toggle('price--sale', Boolean(variant.compareHtml));
      let compare = price.querySelector('.price__compare');
      if (variant.compareHtml) {
        if (!compare) {
          compare = document.createElement('s');
          compare.className = 'price__compare';
          price.append(compare);
        }
        compare.textContent = toText(variant.compareHtml);
      } else compare?.remove();
    }

    const low = this.querySelector('[data-low-stock]');
    if (low) {
      low.hidden = !variant.lowStock;
      if (variant.lowStock && strings.lowStockCount) low.textContent = strings.lowStockCount.replace('[count]', variant.lowStock);
    }

    const meta = this.querySelector('[data-sticky-meta]');
    if (meta) meta.textContent = `${variant.title} · ${toText(variant.priceHtml)}`;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState(window.history.state, '', url);
  }

  filterMedia(color) {
    if (!color || !this.items.length) return;
    const key = color.toLowerCase().trim();
    const hasMatch = this.items.some((li) => li.dataset.alt === key);
    this.items.forEach((li) => {
      const alt = li.dataset.alt;
      li.hidden = hasMatch && this.colorNames.has(alt) && alt !== key;
    });
    this.track.scrollTo({ left: 0 });
    this.updateCounter();
  }

  showMedia(id) {
    const item = this.items.find((li) => li.dataset.mediaId === String(id));
    if (!item) return;
    item.hidden = false;
    this.items.forEach((li) => li.classList.toggle('is-active', li === item));
    if (this.track.firstElementChild !== item) this.track.prepend(item);
    this.track.scrollTo({ left: 0, behavior: 'smooth' });
    if (window.matchMedia('(min-width: 990px)').matches && item.getBoundingClientRect().top < 0) {
      item.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    this.updateCounter();
  }

  initCounter() {
    this.indexEl = this.querySelector('[data-gallery-index]');
    this.totalEl = this.querySelector('[data-gallery-total]');
    this.barEl = this.querySelector('[data-gallery-bar]');
    if (!this.indexEl || !this.track) return;
    let ticking = false;
    this.track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { this.updateCounter(); ticking = false; });
    }, { passive: true });
    this.updateCounter(); // the colour filter ran before the counter existed
  }

  updateCounter() {
    if (!this.indexEl) return;
    const visible = [...this.track.children].filter((li) => !li.hidden);
    const w = this.track.clientWidth || 1;
    const i = Math.min(visible.length, Math.round(Math.abs(this.track.scrollLeft) / w) + 1);
    this.indexEl.textContent = i;
    this.totalEl.textContent = visible.length;
    if (this.barEl) {
      this.barEl.style.inlineSize = `${100 / Math.max(1, visible.length)}%`;
      this.barEl.style.transform = `translateX(${(document.dir === 'rtl' ? -100 : 100) * (i - 1)}%)`;
    }
  }

  initSticky() {
    const bar = this.querySelector('[data-sticky-buy]');
    const buy = this.querySelector('[data-buy]');
    if (!bar || !buy || !('IntersectionObserver' in window)) return;
    const button = bar.querySelector('button');
    // Shown once the buy buttons scroll away, hidden again while the order form is on screen.
    const order = this.querySelector('order-form');
    let pastBuy = false;
    let onOrder = false;
    const sync = () => {
      const show = pastBuy && !onOrder;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      button.tabIndex = show ? 0 : -1;
    };
    new IntersectionObserver(([entry]) => {
      pastBuy = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sync();
    }).observe(buy);
    if (order) {
      new IntersectionObserver(([entry]) => {
        onOrder = entry.isIntersecting;
        sync();
      }).observe(order);
    }
  }

  /* Reels: muted loops that only play while on screen; off under reduced motion. */
  initReels() {
    const reels = [...this.querySelectorAll('[data-reel]')];
    if (!reels.length) return;
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const set = (reel, playing) => {
      const video = reel.querySelector('video');
      const toggle = reel.querySelector('[data-reel-toggle]');
      if (!video) return;
      if (playing) video.play().catch(() => {});
      else video.pause();
      reel.classList.toggle('is-paused', !playing);
      toggle?.setAttribute('aria-label', playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay);
    };
    reels.forEach((reel) => {
      reel.userPaused = calm;
      set(reel, false);
      reel.querySelector('[data-reel-toggle]')?.addEventListener('click', () => {
        reel.userPaused = !reel.classList.contains('is-paused');
        set(reel, !reel.userPaused);
      });
    });
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (!entry.target.userPaused) set(entry.target, entry.isIntersecting);
    }), { threshold: 0.5 });
    reels.forEach((reel) => io.observe(reel));
  }
}

customElements.define('product-page', ProductPage);

/* Size & fit table: cm / in switch. Only cells that hold numbers convert; the weight row stays in kg. */
class FitGuide extends HTMLElement {
  connectedCallback() {
    this.buttons = [...this.querySelectorAll('[data-unit]')];
    this.buttons.forEach((b) => b.addEventListener('click', () => this.setUnit(b.dataset.unit)));
  }

  setUnit(unit) {
    this.buttons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.unit === unit)));
    this.querySelectorAll('[data-cm]').forEach((td) => {
      const cm = td.dataset.cm;
      td.textContent = unit === 'in'
        ? cm.replace(/\d+(?:\.\d+)?/g, (n) => (Number(n) / 2.54).toFixed(1).replace(/\.0$/, ''))
        : cm;
    });
  }
}

customElements.define('fit-guide', FitGuide);

/* Size finder: weight in kg → the size whose weight guide covers it, selected for her.
   Below or above every range, the nearest size is suggested with a short note. */
class SizeFinder extends HTMLElement {
  connectedCallback() {
    const sizes = JSON.parse(this.dataset.sizes || '[]');
    const weights = JSON.parse(this.dataset.weights || '[]');
    this.ranges = sizes.map((size, i) => {
      const [min, max] = (String(weights[i] || '').match(/\d+(?:\.\d+)?/g) || []).map(Number);
      return { size, min, max: max ?? min };
    }).filter((r) => Number.isFinite(r.min));
    this.input = this.querySelector('[data-size-finder-input]');
    this.result = this.querySelector('[data-size-finder-result]');
    this.querySelector('[data-size-finder-go]').addEventListener('click', () => this.find());
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); this.find(); }
    });
  }

  find() {
    const weight = Number(this.input.value);
    if (!this.ranges.length || !weight || weight < 30 || weight > 200) return this.say(this.dataset.msgInvalid, 'invalid');
    const sorted = [...this.ranges].sort((a, b) => a.min - b.min);
    const lo = sorted[0];
    const hi = sorted[sorted.length - 1];
    let pick; let kind;
    if (weight < lo.min) { pick = lo; kind = 'under'; }
    else if (weight > hi.max) { pick = hi; kind = 'over'; }
    else {
      // Overlapping ranges: the one whose middle is nearest. A gap between ranges: the next size up.
      const fits = sorted.filter((r) => weight >= r.min && weight <= r.max);
      pick = fits.sort((a, b) => Math.abs(weight - (a.min + a.max) / 2) - Math.abs(weight - (b.min + b.max) / 2))[0]
        || sorted.find((r) => r.min > weight);
      kind = 'fit';
    }
    const msg = this.dataset[`msg${kind[0].toUpperCase()}${kind.slice(1)}`];
    this.say(msg.replace('[size]', pick.size).replace('[weight]', weight), kind);
    this.select(pick.size);
  }

  select(size) {
    const radio = [...document.querySelectorAll(`input[name="option${this.dataset.option}"]`)]
      .find((r) => r.value === size && !r.disabled);
    if (!radio || radio.checked) return;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  say(text, kind) {
    this.result.textContent = text;
    this.result.dataset.kind = kind;
  }
}

customElements.define('size-finder', SizeFinder);

/* Order form under the details: collects name, mobiles, governorate and address, then opens
   Shopify checkout for this piece with the delivery address filled in (a cart permalink).
   The bag is not touched. Both mobiles also travel as order attributes. */
const formatMoney = (cents, format) => {
  const n = cents / 100;
  const fixed = (d, t = ',', dec = '.') => {
    const [i, f] = n.toFixed(d).split('.');
    return i.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (f ? dec + f : '');
  };
  return (format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => ({
    amount: fixed(2),
    amount_no_decimals: fixed(0),
    amount_with_comma_separator: fixed(2, '.', ','),
    amount_no_decimals_with_comma_separator: fixed(0, '.'),
  }[key] ?? fixed(2)));
};

class OrderForm extends HTMLElement {
  connectedCallback() {
    this.page = this.closest('product-page');
    this.form = this.querySelector('[data-order-form]');
    if (!this.page || !this.form) return;
    this.variants = JSON.parse(this.page.querySelector('[data-variants]')?.textContent || '[]');
    this.delivery = Number(this.dataset.delivery) || 0;
    this.error = this.querySelector('[data-order-error]');
    this.submit = this.querySelector('[data-order-submit]');
    // The product form updates its variant id on the same change event, before it bubbles here.
    this.page.addEventListener('change', () => this.update());
    this.page.addEventListener('input', (e) => { if (e.target.name === 'quantity') this.update(); });
    this.form.addEventListener('submit', (e) => this.onSubmit(e));
    this.form.addEventListener('input', () => { if (this.error) this.error.hidden = true; });
    this.update();
  }

  current() {
    const productForm = this.page.querySelector('form[data-product-form]');
    const id = Number(productForm?.querySelector('[data-variant-id]')?.value);
    const qty = Math.max(1, Number(productForm?.querySelector('[name="quantity"]')?.value) || 1);
    return { variant: this.variants.find((v) => v.id === id), qty };
  }

  update() {
    const { variant, qty } = this.current();
    if (this.submit) this.submit.disabled = !variant?.available;
    if (!variant) return;
    const total = this.querySelector('[data-order-total]');
    if (total) total.textContent = formatMoney(variant.price * qty + this.delivery, this.dataset.moneyFormat);
  }

  onSubmit(event) {
    event.preventDefault();
    const { variant, qty } = this.current();
    const invalid = [...this.form.elements].find((el) => el.willValidate && !el.checkValidity());
    if (invalid) {
      if (this.error) {
        this.error.textContent = invalid.validity.patternMismatch ? this.dataset.msgPhone : this.dataset.msgRequired;
        this.error.hidden = false;
      }
      invalid.focus();
      return;
    }
    if (!variant?.available) return;

    const data = new FormData(this.form);
    const value = (k) => String(data.get(k) || '').trim().replace(/\s+/g, ' ');
    const [first, ...rest] = value('name').split(' ');
    const phone = value('phone').replace(/\s/g, '');
    const extra = value('phone_extra').replace(/\s/g, '');
    // International form (+20…) for the delivery phone. Checkout's Contact box can't take a phone from the link: checkout[email] rejects it ("Email is invalid").
    const mobile = phone.replace(/^(\+?20|0)?(1\d{9})$/, '+20$2');
    const params = new URLSearchParams({
      'checkout[shipping_address][first_name]': first,
      'checkout[shipping_address][last_name]': rest.join(' ') || first,
      'checkout[shipping_address][address1]': value('address'),
      'checkout[shipping_address][city]': value('governorate'),
      'checkout[shipping_address][province]': value('governorate'),
      'checkout[shipping_address][country]': 'Egypt',
      'checkout[shipping_address][phone]': mobile,
      'attributes[Main mobile]': phone,
    });
    if (extra) params.set('attributes[Extra mobile]', extra);
    this.submit?.classList.add('is-loading');
    const root = (this.dataset.root || '/').replace(/\/$/, '');
    window.location.assign(`${root}/cart/${variant.id}:${qty}?${params}`);
  }
}
customElements.define('order-form', OrderForm);
